import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Typography,
} from '@mui/material';
import PageShell from './components/PageShell';
import Snackbar from './components/Snackbar';
import { subscriptionAdminApi, getApiErrorMessage } from '../subscriptionAdmin/api';
import { canWrite } from '../subscriptionAdmin/permissions';
import { formatInr } from '../subscriptionAdmin/razorpay';

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });
  const canIssue = canWrite('invoices');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await subscriptionAdminApi.listInvoices();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const issue = async (id) => {
    try {
      await subscriptionAdminApi.issueInvoice(id);
      setSnackbar({ open: true, type: 'success', message: 'Invoice issued.' });
      load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    }
  };

  return (
    <PageShell
      title="Step 6 — Invoices"
      subtitle="GET /invoices · POST /invoices/:id/issue · GET /invoices/:id/pdf"
    >
      {loading ? (
        <CircularProgress />
      ) : (
        <Box sx={{ overflowX: 'auto', bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>User</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv._id}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {inv.invoiceNumber || String(inv._id).slice(-8)}
                  </TableCell>
                  <TableCell><Chip label={inv.status || '—'} size="small" /></TableCell>
                  <TableCell>{formatInr(inv.totalAmount || inv.amount)}</TableCell>
                  <TableCell>{String(inv.userId || '').slice(-8)}</TableCell>
                  <TableCell align="right">
                    {canIssue && inv.status !== 'issued' && (
                      <Button size="small" onClick={() => issue(inv._id)}>Issue</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!invoices.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No invoices yet (created on paid billing cycles).</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <Snackbar {...snackbar} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} />
    </PageShell>
  );
}
